import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { FileUpload } from "@/components/FileUpload";
import { AvailabilityEditor } from "@/components/AvailabilityEditor";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/api";
import { US_STATES } from "@/lib/usStates";
import api from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

export default function CreateKitchen() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "", description: "", address: "", city: "",
    hourly_rate: 50, min_hours: 2, booking_mode: "approval",
    amenities: [], photos: [], availability: {}, state: "",
  });
  const [amInput, setAmInput] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) { nav("/login"); return null; }
  if (user.role !== "owner") { nav("/browse"); return null; }

  const upd = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const addAmenity = () => {
    if (!amInput.trim()) return;
    upd("amenities", [...f.amenities, amInput.trim()]);
    setAmInput("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f, hourly_rate: Number(f.hourly_rate), min_hours: Number(f.min_hours) };
      const { data } = await api.post("/kitchens", payload);
      toast.success("Kitchen listed");
      nav(`/kitchen/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-14">
        <div className="tick-label">Host</div>
        <h1 className="font-display font-black text-5xl tracking-tighter mt-2">List your kitchen.</h1>

        <form data-testid="new-kitchen-form" onSubmit={submit} className="mt-10 space-y-6">
          <Field label="Kitchen name">
            <input data-testid="k-name" required value={f.name} onChange={e => upd("name", e.target.value)} className="w-full border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]" />
          </Field>
          <Field label="Description">
            <textarea data-testid="k-desc" required value={f.description} onChange={e => upd("description", e.target.value)} rows={5} className="w-full border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]" />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="City">
              <input data-testid="k-city" required value={f.city} onChange={e => upd("city", e.target.value)} className="w-full border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]" />
            </Field>
            <Field label="State">
              <select data-testid="k-state" value={f.state} onChange={e => upd("state", e.target.value)} className="w-full border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]">
                <option value="">Select state…</option>
                {US_STATES.map((s) => (<option key={s.code} value={s.code}>{s.code} · {s.name}</option>))}
              </select>
            </Field>
            <Field label="Address">
              <input data-testid="k-address" required value={f.address} onChange={e => upd("address", e.target.value)} className="w-full border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]" />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="$ per hour">
              <input data-testid="k-rate" type="number" min={1} required value={f.hourly_rate} onChange={e => upd("hourly_rate", e.target.value)} className="w-full border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]" />
            </Field>
            <Field label="Min hours">
              <input data-testid="k-min-hours" type="number" min={1} required value={f.min_hours} onChange={e => upd("min_hours", e.target.value)} className="w-full border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]" />
            </Field>
            <Field label="Booking mode">
              <select data-testid="k-mode" value={f.booking_mode} onChange={e => upd("booking_mode", e.target.value)} className="w-full border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]">
                <option value="approval">Approval required</option>
                <option value="instant">Instant book</option>
              </select>
            </Field>
          </div>

          <Field label="Amenities">
            <div className="flex gap-2">
              <input data-testid="k-am-input" value={amInput} onChange={e => setAmInput(e.target.value)} placeholder="e.g. Pizza oven" className="flex-1 border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]" />
              <button type="button" data-testid="k-am-add" onClick={addAmenity} className="btn-outline">Add</button>
            </div>
            {f.amenities.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {f.amenities.map((a, i) => (
                  <span key={i} className="border hairline px-3 py-1.5 text-sm flex items-center gap-2">
                    {a} <button type="button" onClick={() => upd("amenities", f.amenities.filter((_,j)=>j!==i))}><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </Field>

          <Field label="Weekly availability">
            <AvailabilityEditor value={f.availability} onChange={(v) => upd("availability", v)} />
            <p className="text-neutral-500 text-xs mt-2">Leave blank to allow bookings any time.</p>
          </Field>

          <Field label="Photos">
            <FileUpload
              testId="k-photo-upload"
              label="Add photo"
              accept="image/*"
              onUploaded={(res) => upd("photos", [...f.photos, res.path])}
            />
            {f.photos.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-3">
                {f.photos.map((p, i) => (
                  <div key={i} className="relative aspect-square bg-neutral-900 border hairline overflow-hidden">
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => upd("photos", f.photos.filter((_,j)=>j!==i))} className="absolute top-1 right-1 bg-black/70 p-1"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </Field>

          <button data-testid="k-submit" type="submit" disabled={busy} className="btn-signal disabled:opacity-50">
            {busy ? "..." : "Publish kitchen"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="tick-label mb-2">{label}</div>
      {children}
    </div>
  );
}
